import { useLoginInfo } from '@/components/LoginInfo/provider';
import { useCallback, useState } from 'react';

type UseLayoutPreferenceArgs<T extends string> = {
  storageKey: string;
  defaultLayout: T;
  validValues: readonly T[];
};

const isBrowser = typeof window !== 'undefined';

const readStoredLayout = <T extends string>(
  storageKey: string,
  defaultLayout: T,
  validValues: readonly T[]
): T => {
  if (!isBrowser) return defaultLayout;

  const stored = window.localStorage.getItem(storageKey);
  if (stored === null) {
    return defaultLayout;
  }
  const match = validValues.find((value) => value === stored);
  if (match !== undefined) {
    return match;
  }
  // Unexpected/corrupt value (e.g. stale format) — clear it.
  window.localStorage.removeItem(storageKey);
  return defaultLayout;
};

/**
 * Shared persistence for the 支払明細 / 仕訳 card-vs-table layout toggles
 * (NA-5531). Reads the last-used layout from localStorage on mount and
 * writes on every change, scoped per user (mirrors `useDrawerWidth`).
 */
export const useLayoutPreference = <T extends string>({
  storageKey,
  defaultLayout,
  validValues,
}: UseLayoutPreferenceArgs<T>) => {
  const { currentUser } = useLoginInfo();
  const userId = currentUser?.id;

  const [layout, setLayoutState] = useState<T>(() => {
    if (!userId) return defaultLayout;
    return readStoredLayout(
      `${storageKey}_${userId}`,
      defaultLayout,
      validValues
    );
  });

  const setLayout = useCallback(
    (next: T) => {
      setLayoutState(next);
      if (isBrowser && userId) {
        window.localStorage.setItem(`${storageKey}_${userId}`, next);
      }
    },
    [storageKey, userId]
  );

  return {
    layout,
    setLayout,
  };
};
